import * as turf from '@turf/turf';
import type { Feature, Geometry, MultiPolygon, Polygon } from 'geojson';
import {
  buildGeoLocationFromGeoJson,
  type GeoLocation,
} from 'components/GeoLocationComponent/buildGeoLocationFromGeoJson';

export const MAX_GEOJSON_AORS = 200;

const NAME_KEYS = ['name', 'NAME', 'Name', 'title', 'label', 'LABEL'];

export type ParsedGeoJsonAor = {
  name: string;
  description: string;
  geolocation: GeoLocation;
};

export type GeoJsonAorParseResult = {
  aors: ParsedGeoJsonAor[];
  /** Features dropped because they are not polygons. */
  skipped: number;
  /** Polygon features past MAX_GEOJSON_AORS. */
  truncated: number;
};

function isArea(geometry: Geometry | null): geometry is Polygon | MultiPolygon {
  return geometry?.type === 'Polygon' || geometry?.type === 'MultiPolygon';
}

function featureName(
  properties: Record<string, unknown> | null,
  fallback: string
): string {
  for (const key of NAME_KEYS) {
    const value = properties?.[key];
    if (typeof value === 'string' && value.trim()) return value.trim();
    if (typeof value === 'number') return String(value);
  }
  return fallback;
}

function featuresOf(json: unknown): Feature[] {
  if (!json || typeof json !== 'object') return [];
  const value = json as { type?: string; features?: unknown };
  if (value.type === 'FeatureCollection' && Array.isArray(value.features)) {
    return value.features as Feature[];
  }
  if (value.type === 'Feature') return [json as Feature];
  if (typeof value.type === 'string') {
    return [{ type: 'Feature', geometry: json as Geometry, properties: {} }];
  }
  return [];
}

/**
 * One AOR per polygon feature of a GeoJSON file. Throws when the text is not
 * JSON or holds no polygons, so the caller can show the message as-is.
 */
export function parseGeoJsonAors(
  text: string,
  fileLabel: string
): GeoJsonAorParseResult {
  let json: unknown;
  try {
    json = JSON.parse(text);
  } catch {
    throw new Error(`${fileLabel} is not valid GeoJSON.`);
  }
  const features = featuresOf(json);
  const areas = features.filter((feature) => isArea(feature?.geometry));
  if (areas.length === 0) {
    throw new Error(`${fileLabel} has no polygon features.`);
  }
  const kept = areas.slice(0, MAX_GEOJSON_AORS);
  const aors = kept.map((feature, index) => {
    const geometry = feature.geometry as Polygon | MultiPolygon;
    const name = featureName(
      feature.properties as Record<string, unknown> | null,
      kept.length === 1 ? fileLabel : `${fileLabel} ${index + 1}`
    );
    return {
      name,
      description: '',
      geolocation: buildGeoLocationFromGeoJson({
        geometry,
        centroid: turf.centroid(geometry),
        placeName: name,
      }),
    };
  });
  return {
    aors,
    skipped: features.length - areas.length,
    truncated: areas.length - kept.length,
  };
}

export function fileLabelOf(fileName: string): string {
  return fileName.replace(/\.(geo)?json$/i, '').trim() || 'Uploaded area';
}
